import { useMemo } from "react";

import moment from "moment";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useMembers } from "@/contexts/member-context";
import { cn } from "@/lib/utils";
import type { IIdentity } from "@/providers/auth-provider";

export interface ResolvedBy {
  displayName: string;
  photoUrl: string | null;
}

/**
 * The curator behind a resolved HITL, matched against the admin member list.
 *
 * An unrecognised uid prints **"Former curator"** once the member list has
 * loaded — the same word `/tina/home`'s leaderboard uses for a curator who has
 * since left. Before it lands (the list is fetched once, on mount, app-wide) the
 * uid prints as itself instead: an empty member list would otherwise label
 * every resolver a former one.
 *
 * **A NULL uid is not a "no".** Tina stamps `hitl_resolved_by` only when the
 * caller passed one, and a park resolved before migration `0027` was never
 * backfilled — so an absent name means "unknown", never "nobody reviewed this".
 * `hitl_reason` answers whether the item went through review at all.
 */
export function resolverFor(
  uid: string | null | undefined,
  members: IIdentity[],
): ResolvedBy | null {
  if (!uid) return null;
  const member = members.find((m) => m.id === uid);
  if (member) {
    return {
      displayName: member.displayName || member.email || uid,
      photoUrl: member.photoUrl || null,
    };
  }
  return {
    displayName: members.length > 0 ? "Former curator" : uid,
    photoUrl: null,
  };
}

function resolverTooltip(
  resolver: ResolvedBy,
  resolvedAt: string | null | undefined,
): string {
  return resolvedAt
    ? `${resolver.displayName} · resolved ${moment(resolvedAt).format("D MMM YYYY HH:mm")}`
    : resolver.displayName;
}

/**
 * `resolverFor` against the app-wide member list, memoised so a re-render does
 * not re-scan it. Both the Tina Trace card and the item show header go through
 * here — the "who resolved it" rule has exactly one owner.
 */
export function useHitlResolver(
  uid: string | null | undefined,
): ResolvedBy | null {
  const { members } = useMembers();
  return useMemo(() => resolverFor(uid, members), [uid, members]);
}

interface ResolvedByChipProps {
  /** The item's `hitl_resolved_by`. Renders nothing when absent. */
  uid: string | null | undefined;
  /** The item's `hitl_resolved_at`, for the tooltip. */
  resolvedAt?: string | null;
  className?: string;
}

/**
 * The curator who cleared an item's HITL park, as a photo and a name.
 *
 * Renders **nothing** when no resolver was recorded, so a caller may mount it
 * unconditionally — but a caller that also wants a label or a separator around
 * it has to guard on `uid` itself.
 */
export function ResolvedByChip({
  uid,
  resolvedAt,
  className,
}: ResolvedByChipProps) {
  const resolver = useHitlResolver(uid);
  if (!resolver) return null;

  return (
    <span
      className={cn("inline-flex min-w-0 items-center gap-1.5", className)}
      title={resolverTooltip(resolver, resolvedAt)}
    >
      <Avatar className="h-5 w-5 shrink-0">
        <AvatarImage src={resolver.photoUrl ?? undefined} />
        <AvatarFallback className="text-[9px]">
          {resolver.displayName.charAt(0).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <span className="truncate text-xs font-medium text-foreground">
        {resolver.displayName}
      </span>
    </span>
  );
}
